import { createStyles, Grid, makeStyles, MenuItem, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import DescriptionIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import { DataGridPro, getGridStringOperators, GridCellParams, GridFilterModel, GridSortModel, GridToolbarContainer, GridValueGetterParams } from '@mui/x-data-grid-pro';
import { useGenericStyles } from 'common/genericStyles';
import { FuncaoParticipante, GrupoAcesso, InvestidoresFilter, InvestidorResponse, Perfil, TipoInvestidor } from 'common/genericTypes';
import { GenericDrawer } from 'components/genericDrawer';
import { GenericToast, GenericToastKind } from 'components/genericToast';
import { Button, MdSpace, MoreOptionsMenu } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import DataGridToolbar from 'utils/dataGrid/xlsxExportToolbar';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { addCpfCnpjMask, addTelefoneMask } from 'utils/mask';
import { v4 as uuid } from 'uuid';
import { SaveAlt } from '@material-ui/icons';

const useStyles = makeStyles(() =>
  createStyles({
    telefone: {
      width: '100px',
    },
    contaDeposito: {
      width: '120px',
    },
    cpfCnpj: {
      width: '150px',
    },
  })
);

enum SuspenderLiberarActionDrawer {
  Suspender = 'SUSPENDER',
  Liberar = 'LIBERAR',
}

const ListaInvestidores: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const genericClasses = useGenericStyles();

  const {
    getInvestidores: getInvestidoresApi,
    deleteInvestidor,
    postSuspenderInvestidor,
    postLiberarSuspensaoInvestidor,
    postInvestidorPaginated,
  } = useSelector((state: RootState) => state.investidorApi);
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const isParticipanteCustodiante = useSelector(
    (state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante
  );
  const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes);
  const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes);

  const [investidores, setInvestidores] = useState<Array<InvestidorResponse>>([]);
  const [selectedInvestidor, setSelectedInvestidor] = useState<InvestidorResponse>();
  const [disabledInvestidor, setDisabledInvestidor] = useState<InvestidorResponse>();
  const [investidorSuspenso, setInvestidorSuspenso] = useState<InvestidorResponse>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openSuspenderLiberarDrawer, setOpenSuspenderLiberarDrawer] = useState<boolean>(false);
  const [suspenderLiberarActionDrawer, setSuspenderLiberarActionDrawer] =
    useState<SuspenderLiberarActionDrawer | null>();
  const [submitting, setSubmitting] = useState(false);
  const [optionsKeyReset, setOptionsKeyReset] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [investidoresFilter, setInvestidoresFilter] = useState<Array<InvestidoresFilter>>([]);

  const getInvestidores = useCallback(async () => {
    try {
      const result = await postInvestidorPaginated(
        dispatch,
        {
          page: page + 1,
          pageSize,
          linkOperator: linkOperator,
          investidoresFilter: investidoresFilter,
        },
        true
      );
      setInvestidores(result.result);
      setPageCount(result.totalCount);
    } catch { }
  }, [investidoresFilter, pageSize, page, linkOperator]);

  const handleDisableClick = async () => {
    setSubmitting(true);
    try {
      await deleteInvestidor(dispatch, selectedInvestidor?.id, true);
      setOpenDrawer(false);
      setDisabledInvestidor(selectedInvestidor);
      getInvestidores();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspenderClick = async () => {
    if (!selectedInvestidor) return;
    try {
      await postSuspenderInvestidor(dispatch, selectedInvestidor.id, true);
      setOpenDrawer(false);
      setInvestidorSuspenso(selectedInvestidor);
      getInvestidores();
    } catch (error) {
    } finally {
      handleSuspenderLiberarDrawerClose();
    }
  };

  const handleLiberarSuspensaoClick = async () => {
    if (!selectedInvestidor) return;
    try {
      await postLiberarSuspensaoInvestidor(dispatch, selectedInvestidor.id, true);
      setOpenDrawer(false);
      setInvestidorSuspenso(selectedInvestidor);
      getInvestidores();
    } catch (error) {
    } finally {
      handleSuspenderLiberarDrawerClose();
    }
  };

  useEffect(() => {
    getInvestidores();
  }, [getInvestidores]);

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleSuspenderLiberarDrawerClose = () => {
    setOpenSuspenderLiberarDrawer(false);
  };
  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setInvestidoresFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      const newInvestidoresFilter = new Array<InvestidoresFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as InvestidoresFilter;
          newInvestidoresFilter.push(params);
        }
      });
      setInvestidoresFilter(newInvestidoresFilter);
    },
    [getInvestidores, page, pageSize, linkOperator, investidoresFilter]
  );

  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      const newInvestidorFilter = investidoresFilter.filter(x => x.orderByType != "ASC" && x.orderByType != "DESC") as Array<InvestidoresFilter>;
      if (filterModel.length === 0 || filterModel == null ) {
        setInvestidoresFilter([...newInvestidorFilter]);
        return;
      }

      const filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as InvestidoresFilter;

      newInvestidorFilter.push(params);
      setInvestidoresFilter([...newInvestidorFilter]);
    },
    [getInvestidores, page, pageSize, investidoresFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith', 'is'].includes(value)
  );

  const getGridColumns = useCallback(
    () => [
      {
        field: 'CONTA_DEPOSITO',
        headerName: 'Conta Depósito',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).contaDeposito,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item>
                <Typography className={classes.contaDeposito} variant="body2">
                  {investidor.contaDeposito}
                </Typography>
              </Grid>
            </>
          );
        },
      },
      {
        field: 'RAZAO_SOCIAL',
        headerName: 'Razão Social',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).razaoSocial,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item xl={3} md={3}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={investidor.razaoSocial} aria-label={investidor.razaoSocial}>
                      <Typography className={genericClasses.wrapTxt} variant="body2">
                        {investidor.razaoSocial}
                      </Typography>
                    </Tooltip>
                  </div>
                </div>
              </Grid>
            </>
          );
        },
      },
      {
        field: 'SUSPENSO',
        headerName: 'Suspenso',
        type: "singleSelect",
        valueOptions: ["SIM", "NÃO"],
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 100,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).suspenso ?  "SIM" : "NÃO",
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              {investidor.suspenso && (
                <div style={{ display: 'flex', alignItems: 'center', width: 40 }}>
                  <Tooltip title={'Suspenso'} aria-label={'Suspenso'} >
                    <ErrorIcon fontSize="small" className={genericClasses.errorIcon} />
                  </Tooltip>
                </div>
              )}
            </>
          );
        },
      },
      {
        field: 'CPF_CNPJ',
        headerName: 'CPF/CNPJ',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).cpfCnpj,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item>
                <Typography className={classes.cpfCnpj} variant="body2">
                  {addCpfCnpjMask(investidor.cpfCnpj)}
                </Typography>
              </Grid>
            </>
          );
        },
      },
      {
        field: 'EMAIL',
        headerName: 'E-mail',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).email,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Tooltip title={investidor.email} aria-label={investidor.email}>
                <Typography className={genericClasses.wrapTxt} variant="body2">
                  {investidor.email}
                </Typography>
              </Tooltip>
            </>
          );
        },
      },
      {
        field: 'TELEFONES',
        headerName: 'Telefones',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).telefones[0],
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item>
                <Typography variant="body2" className={classes.telefone}>
                  {addTelefoneMask(investidor.telefones[0])}
                </Typography>
              </Grid>
            </>
          );
        },
      },
      {
        field: 'RAZAO_SOCIAL_CUSTODIANTE',
        headerName: 'Razão Social do Custodiante',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).razaoSocialCustodiante,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item xl={3} md={3}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={investidor.razaoSocialCustodiante} aria-label={investidor.razaoSocialCustodiante}>
                      <Typography className={genericClasses.wrapTxt} variant="body2">
                        {investidor.razaoSocialCustodiante}
                      </Typography>
                    </Tooltip>
                  </div>
                </div>
              </Grid>
            </>
          );
        },
        hide: !isUsuarioOperacoes && !isUsuarioCadastroOperacoes,
      },
      {
        field: 'CNPJ_CUSTODIANTE',
        headerName: 'CNPJ do Custodiante',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InvestidorResponse).cnpjCustodiante,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <Grid item>
                <Typography className={classes.cpfCnpj} variant="body2">
                  {addCpfCnpjMask(investidor.cnpjCustodiante)}
                </Typography>
              </Grid>
            </>
          );
        },
        hide: !isUsuarioOperacoes && !isUsuarioCadastroOperacoes,
      },
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        renderCell: (params: GridCellParams) => {
          const investidor = params.row as InvestidorResponse;
          return (
            <>
              <MoreOptionsMenu key={optionsKeyReset}>
                {isParticipanteCustodiante && !isUsuarioLeitura && (
                  <MenuItem onClick={() => {
                    if (investidor.tipo === TipoInvestidor.Fundo) {
                      history.push(`/gerenciar/investidores/editar/${investidor.id}?tipo=fundo`);
                    } else {
                      history.push(`/gerenciar/investidores/editar/${investidor.id}`);
                    }
                  }}>
                    <EditIcon fontSize="small" style={{ marginRight: '10px' }} />
                    {'Editar'}
                  </MenuItem>
                )}
                <MenuItem onClick={() => history.push(`/gerenciar/investidores/detalhes/${investidor.id}`)}>
                  <DescriptionIcon fontSize="small" style={{ marginRight: '10px' }} />
                  {'Ver Detalhes'}
                </MenuItem>
                {isParticipanteCustodiante && !isUsuarioLeitura && (
                  <MenuItem
                    onClick={() => {
                      setOptionsKeyReset(uuid());
                      setSelectedInvestidor(investidor);
                      setOpenDrawer(true);
                    }}
                  >
                    <CancelIcon fontSize="small" style={{ marginRight: '10px' }} />
                    {'Desativar investidor'}
                  </MenuItem>
                )}
                {isUsuarioOperacoes && !isUsuarioLeitura || isParticipanteCustodiante ? (
                  investidor.suspenso ? (
                    <MenuItem
                      onClick={() => {
                        setInvestidorSuspenso(undefined);
                        setOptionsKeyReset(uuid());
                        setSelectedInvestidor(investidor);
                        setSuspenderLiberarActionDrawer(SuspenderLiberarActionDrawer.Liberar);
                        setOpenSuspenderLiberarDrawer(true);
                      }}
                    >
                      <DescriptionIcon fontSize="small" style={{ marginRight: '10px' }} />
                      {'Liberar suspensão'}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => {
                        setInvestidorSuspenso(undefined);
                        setOptionsKeyReset(uuid());
                        setSelectedInvestidor(investidor);
                        setSuspenderLiberarActionDrawer(SuspenderLiberarActionDrawer.Suspender);
                        setOpenSuspenderLiberarDrawer(true);
                      }}
                    >
                      <CancelIcon fontSize="small" style={{ marginRight: '10px' }} />
                      {'Suspender'}
                    </MenuItem>
                  )
                ) : (
                  <></>
                )}
              </MoreOptionsMenu>
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      {!!disabledInvestidor && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setDisabledInvestidor(undefined)}>
            <Typography variant="body2" display={'inline'}>
              {'Investidor'}
            </Typography>
            &nbsp;<Typography display={'inline'}>{`${selectedInvestidor?.razaoSocial} desativado`}</Typography>&nbsp;
            <Typography variant="body2" display={'inline'}>
              {'com sucesso'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      {!!investidorSuspenso && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setInvestidorSuspenso(undefined)}>
            <Typography variant="body2" display={'inline'}>
              {'Investidor'}
            </Typography>
            &nbsp;
            <Typography display={'inline'}>{`${selectedInvestidor?.razaoSocial} ${suspenderLiberarActionDrawer === SuspenderLiberarActionDrawer.Suspender ? 'suspenso' : 'liberado'
            } `}</Typography>
            &nbsp;
            <Typography variant="body2" display={'inline'}>
              {'com sucesso'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}


      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={investidores}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      <MdSpace />
      <GenericDrawer
        opened={openDrawer}
        title={'Desativar Investidor'}
        textContents={[{ text: `Tem certeza que deseja desativar o investidor ${selectedInvestidor?.razaoSocial} ?` }]}
        onClose={handleDrawerClose}
        buttons={[
          {
            buttonText: 'Desativar Investidor',
            buttonAction: handleDisableClick,
            buttonColor: 'error',
          },
          {
            buttonText: 'Cancelar',
            buttonAction: handleDrawerClose,
          },
        ]}
        disableButtons={submitting}
      />
      <GenericDrawer
        opened={openSuspenderLiberarDrawer}
        title={`${suspenderLiberarActionDrawer === SuspenderLiberarActionDrawer.Suspender ? 'Suspender' : 'Liberar'
        } Investidor`}
        textContents={[
          {
            text: `Tem certeza que deseja ${suspenderLiberarActionDrawer === SuspenderLiberarActionDrawer.Suspender ? 'Suspender' : 'Liberar'
            } ${selectedInvestidor?.razaoSocial}?`,
          },
        ]}
        onClose={handleDrawerClose}
        buttons={[
          {
            buttonText: `${suspenderLiberarActionDrawer === SuspenderLiberarActionDrawer.Suspender ? 'Suspender' : 'Liberar'
            } Investidor`,
            buttonAction:
              suspenderLiberarActionDrawer === SuspenderLiberarActionDrawer.Suspender
                ? handleSuspenderClick
                : handleLiberarSuspensaoClick,
            buttonColor: 'primary',
          },
          {
            buttonText: 'Cancelar',
            buttonAction: handleSuspenderLiberarDrawerClose,
          },
        ]}
        disableButtons={submitting}
      />
    </>
  );
};

export default ListaInvestidores;
